import { isDamaged } from "@redotech/redo-model/return";
import { ReturnOptionMethod } from "@redotech/redo-model/return-flow";
import { ReturnTotalsCalculator } from "@redotech/redo-model/return-totals-calculator";
import { ExchangeGroup } from "@redotech/redo-model/team";
import { getLocations, getProduct, getSettings } from "../api";

export async function setFavicon() {
  const response = await getSettings();
  const settings = response?.data;
  // https://app.clickup.com/t/868680b85
  const url = settings?.theme.favicon_url || settings?.theme.logo_url;
  if (url) {
    const link = document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "shortcut icon";
    link.href = url;
    document.head.appendChild(link);
  }
}

export function optional<T, V>(
  value: T | null | undefined,
  fn: (value: T) => V,
): V | null {
  if (value === null || value === undefined) {
    return null;
  }

  return fn(value);
}

const getShowAdjustment = (adjustment) => {
  return adjustment.flat !== 0 || adjustment.proportion !== 1;
};

const getAdjustmentValue = (adjustment, discountPrice) => {
  const adjustmentValue =
    parseFloat(discountPrice) * (adjustment.proportion - 1.0) + adjustment.flat;

  return adjustmentValue;
};

export const apiOutput = (item, currentOrder) => {
  return {
    order_id: currentOrder._id,
    type:
      item.type ||
      (item.green_return
        ? "green_return"
        : item.selected_return_option.method.type ===
            ReturnOptionMethod.REFUND ||
          item.selected_return_option.method.type === ReturnOptionMethod.CREDIT
        ? "return"
        : "exchange"),
    strategy:
      item.strategy ||
      (item.green_return
        ? "green_return"
        : item.selected_return_option.method.type ===
          ReturnOptionMethod.EXCHANGE
        ? "store_credit"
        : item.selected_return_option.method.type === ReturnOptionMethod.REFUND
        ? "refund"
        : "store_credit"),
    line_item_id: item.id,
    quantity: 1,
    tags: item.tags,
    returnReason: item.returnReason,
    price_adjustment:
      item.price_adjustment ||
      (getShowAdjustment(item.selected_return_option.adjustment)
        ? getAdjustmentValue(
            item.selected_return_option.adjustment,
            item.discount_price,
          )
        : "0"),
    exchangeType: item.exchangeType,
  };
};

export const extractPriceFromProperties = (lineItem) => {
  const originItemPrice = optional(
    lineItem.properties.find(({ name }) => name === "_redo_origin_item"),
    ({ value }) => {
      const payload: {
        unitPrice?: string;
      } = JSON.parse(value);

      return payload?.unitPrice || null;
    },
  );

  return originItemPrice
    ? {
        original_price: originItemPrice,
        discount_price: originItemPrice,
      }
    : null;
};

export const hasRedoDiscountCode = (data) => {
  return data.shopify.discount_applications?.some(
    (discount) => discount.code?.includes("REDO"),
  );
};

export const makeOrder = async (data) => {
  const tempData = data;
  const lineItems = data.shopify.line_items.filter(
    (item) =>
      !/^redo$|^re:do$/i.test(item.vendor) && // milwaukeemotorcycleclothing changed the vendor :/
      !/route-/i.test(item.sku) &&
      !/routeins/i.test(item.sku) &&
      !/shipping|insurance|package\s+protection/i.test(item.name),
  );
  const productIds = [];
  lineItems.forEach((item) => {
    if (!productIds.includes(item.product_id)) {
      productIds.push(item.product_id);
    }
  });

  const products = [];
  await Promise.all(
    productIds.map(async (productId) => {
      products.push((await getProduct(productId)).data);
    }),
  );
  const modifiedLineItems = lineItems.map((item) => {
    const extractedPrice = extractPriceFromProperties(item);
    // Won't make api request if it already has for that product.
    let product = products.find((product) => product.id === item.product_id);

    return {
      ...item,
      ...{
        processed_at: new Date(data.shopify.processed_at),
        // now being done with FRE
        // available_strategies: {
        //   refund: item.options.refunds,
        //   exchange: item.options.exchange,
        //   store_credit: item.options.store_credit,
        // },
        shipping_fees: {
          store_credit: 0,
          exchange: 0,
          refund: 0,
        },
        coverage: {
          exchange: data.protected && data.coverage.exchange,
          refund: data.protected && data.coverage.refund,
          store_credit: data.protected && data.coverage.storeCredit,
        },
        original_price: item.price,
        discount_price: ReturnTotalsCalculator.getDiscountPrice(item, data),
        green_return_selected: false,
        green_return_type: "discount", //Default value
        return: null, // This is to define what we do with the product.
        product: product,
        fixed_strategies: [],
        is_exchange_order: data.isExchangeOrder,
        is_defective: null,
        is_wrong_item: null,
        needs_manual_review: false,
        // We use this flag to determine if we should split this single item in a new
        // return. This isu used for returns that require approval by the merchant.
        split: false,
        inputAnswers: [], // should be type InputAnswer
        readyToReturn: false, // Set this to true once user has completed flow for item.
        selected_return_option: null,
        selected_quantity: 0,
        new_item: null,
        returnReason: undefined,
        upsell: undefined,
        restocking_fee: undefined,
        initialLineItem: undefined,
        ...extractedPrice,
      },
    };
  });
  tempData.line_items = modifiedLineItems;
  return tempData;
};

export const enum LocationCondition {
  PRODUCT_TAGS = "product_tags",
  DAMAGED = "damaged",
  NAME = "name",
}

export const exchangeGroupFilter = (
  product,
  exchangeGroup: ExchangeGroup,
  collectionProducts = null,
) => {
  if (!exchangeGroup) {
    return true;
  }
  switch (exchangeGroup.type) {
    case "tag:tag":
      return product.tags.some((tag: string) =>
        exchangeGroup.targetTags.includes(tag),
      );
    case "tag:collection":
    case "name:collection":
      return collectionProducts?.some(
        (collectionProduct) => collectionProduct.id === product.id,
      );
  }
};

async function getFulfilledFromAddress(locationId) {
  const response = await getLocations();

  const location = response.data.locations.find(
    (location) => location.id === locationId,
  );
  if (location) {
    return {
      city: location.city,
      country: location.country,
      country_name: location.country_name,
      name: location.name,
      phone: location.phone,
      state: location.province_code,
      street1: location.address1,
      street2: location.address2,
      zip: location.zip,
    };
  } else {
    // No original location
    return null;
  }
}

async function getAddress(address, originalAddress, originalLocation) {
  if (originalLocation && originalAddress) {
    return originalAddress;
  }
  return address;
}
export const getMerchantAddress = async (
  locationSettings,
  productTags,
  returnReason,
  fulfilledFromLocationId,
) => {
  const fulfilledFromLocation = await getFulfilledFromAddress(
    fulfilledFromLocationId,
  );
  for (const location of locationSettings) {
    switch (location.condition) {
      case LocationCondition.PRODUCT_TAGS:
        const locationTags = location.tags.map((tag) => tag.toLowerCase());
        const matchingTags = productTags.filter((tag) =>
          locationTags.includes(tag.toLowerCase()),
        );
        if (matchingTags.length > 0) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      case LocationCondition.DAMAGED:
        if (isDamaged(returnReason)) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      case LocationCondition.NAME:
        if (fulfilledFromLocation.name === location.address.name) {
          return location.address;
        }
        break;
      default:
        return getAddress(
          location.address,
          fulfilledFromLocation,
          location.originalLocation,
        );
        break;
    }
  }
};

const emailRegex = /\S+@\S+\.\S+/;
export const isValidEmail = (email: string): boolean => emailRegex.test(email);
